'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Check } from 'lucide-react';
import {
  Section,
  SectionHeader,
  SignupCTA,
  Reveal,
  TYPE,
  RADII,
  SHADOW,
} from '../_shared';
import {
  type Currency,
  getCurrencySpec,
  formatPrice,
  DEFAULT_CURRENCY,
} from '@/lib/pricing/currency';

interface PricingV3Props {
  /**
   * Server-resolved currency for the visitor. Falls back to EUR if the
   * parent component is rendered without geo information (e.g., test
   * pages, storybook).
   */
  initialCurrency?: Currency;
}

/**
 * Section 8 — Pricing v3
 *
 * One plan. Segmented Monthly/Yearly toggle.
 * Currency is resolved server-side (Vercel geo header) and can be
 * overridden via the inline switcher — persisted in a cookie so the
 * next page render is already in the chosen currency.
 */
export default function PricingV3({
  initialCurrency = DEFAULT_CURRENCY,
}: PricingV3Props) {
  const t = useTranslations('landing.pricingV3');
  const [yearly, setYearly] = useState(false);
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  // Currency ist read-only auf der Landing: vom Server resolved (Geo + Cookie
  // + Default) und für die ganze Page-Session fixiert. Override findet — falls
  // nötig — nach Signup in Account-Settings statt (F-500-2026 Pattern).
  const currency: Currency = initialCurrency;
  const spec = getCurrencySpec(currency);
  const { prices } = spec;

  const featureCount = 15;
  const addonCount = 3;
  const features = Array.from({ length: featureCount }, (_, i) =>
    t(`plan.features.${i}`),
  );
  const addons = Array.from({ length: addonCount }, (_, i) =>
    t(`plan.addons.${i}`),
  );

  const yearlyBadge = t('toggle.yearlyBadge', {
    savings: formatPrice(prices.savings, currency),
  });
  const savingsPill = t('plan.savingsPill', {
    savings: formatPrice(prices.savings, currency),
  });
  const yearlyTotalLine = t('plan.yearlyTotal', {
    yearlyTotal: formatPrice(prices.yearlyTotal, currency),
    monthlyTotal: formatPrice(prices.monthlyTotal, currency),
  });
  const strikeAria = t('plan.strikeAria', {
    monthly: formatPrice(prices.monthly, currency),
  });

  return (
    <Section id="pricing" tone="muted" ariaLabelledBy="pricing-heading">
      {/* Radial blue glow behind section */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute left-1/2 top-1/3 h-[600px] w-[1000px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-100/60 blur-3xl" />
      </div>

      <SectionHeader
        eyebrow={t('eyebrow')}
        headline={t('headline')}
        description={t('sub')}
        headlineId="pricing-heading"
        size="xl"
        centered
      />

      {/* Currency-Switcher entfernt 2026-05-25 — F-500-2026 Pattern (Stripe,
          Linear, Vercel, Notion, Cal.com, Calendly): kein Switcher auf der
          Pricing-Page. Cognitive Load + Loss-Aversion-Trigger in der
          Conversion-Phase. Geo-Detection via Vercel-Header resolved für 99%
          die richtige Währung; die 5% Edge-Cases (VPN, Cross-Border-B2B)
          ändern es nach Signup in den Account-Settings. Currency-Resolution-
          Pipeline (Geo → Cookie → Default) bleibt unverändert; nur die UI
          ist weg. `onSwitchCurrency` + `setCurrency` werden nicht mehr
          benutzt, der State wird einmalig vom Server initialisiert. */}

      {/* Billing toggle — light style */}
      <Reveal>
        <div className="mt-4 flex justify-center">
          <div
            role="tablist"
            aria-label="Abrechnungsintervall"
            className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white p-1 shadow-sm"
          >
            <button
              type="button"
              role="tab"
              aria-selected={!yearly}
              onClick={() => setYearly(false)}
              className={`h-9 rounded-full px-5 text-sm font-medium transition-colors ${
                !yearly
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-950'
              }`}
            >
              {t('toggle.monthly')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={yearly}
              onClick={() => setYearly(true)}
              className={`relative inline-flex h-9 items-center gap-2 rounded-full px-5 text-sm font-medium transition-colors ${
                yearly
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-950'
              }`}
            >
              {t('toggle.yearly')}
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  yearly ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-700'
                }`}
              >
                {yearlyBadge}
              </span>
            </button>
          </div>
        </div>
      </Reveal>

      {/* Plan Card — white on light section (Umbau 2026-07: Sektion hell) */}
      <Reveal delay={80} trigger="late">
        <div
          className={`relative mx-auto mt-10 max-w-2xl overflow-hidden ${RADII.card} border border-gray-200/80 bg-white text-gray-950 ${SHADOW.hero}`}
        >
          <div className="p-7 sm:p-10">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p
                  className={`inline-flex items-center gap-2 ${TYPE.labelMd} text-blue-600`}
                >
                  {t('plan.name')}
                </p>
                <p className="mt-2 max-w-md text-sm text-gray-600">
                  {t('plan.tagline')}
                </p>
              </div>
            </div>

            {/* Price.
                Loss-aversion: when yearly is active we render the
                discounted price next to a strikethrough of the
                monthly equivalent. Behavioural-pricing research (and
                ConversionXL A/B work) consistently shows that visible
                "what you would otherwise pay" beats percentage-only
                framing — the contrast makes the saving concrete. */}
            <div className="mt-8 flex flex-wrap items-baseline gap-x-2 gap-y-1 tabular-nums">
              {spec.symbolPosition === 'prefix' && (
                <span className="text-sm font-medium text-gray-500">
                  {spec.symbol}
                </span>
              )}
              <span className="text-5xl font-semibold tracking-tight sm:text-6xl">
                {yearly ? prices.yearlyPerMonthDisplay : prices.monthly}
              </span>
              {spec.symbolPosition === 'suffix' && (
                <span className="text-sm font-medium text-gray-500">
                  {spec.symbol}
                </span>
              )}
              <span className="text-sm font-medium text-gray-500">
                {t('plan.perMonth')}
              </span>
              {yearly && (
                <span
                  aria-label={strikeAria}
                  className="text-lg font-medium text-gray-400 line-through decoration-gray-300 sm:ml-1"
                >
                  {formatPrice(prices.monthly, currency)}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-400">{t('plan.vat')}</p>
            {/* Pill + Total bleiben gemountet mit reserviertem Platz:
                Transition statt Mount-Keyframe (Emil: Keyframes auf rapid-
                toggle-UI restarten hart), kein Layout-Sprung beim Toggle. */}
            <div className="min-h-[3.25rem]">
              <p
                aria-hidden={!yearly}
                className={`mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 transition-[opacity,transform] duration-200 ease-out ${
                  yearly ? 'opacity-100 scale-100' : 'pointer-events-none opacity-0 scale-95'
                }`}
              >
                <span aria-hidden="true">✓</span>
                {savingsPill}
              </p>
              <p
                aria-hidden={!yearly}
                className={`mt-2 text-xs text-gray-500 transition-opacity duration-200 ${
                  yearly ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {yearlyTotalLine}
              </p>
            </div>

            {/* #2 ROI-Frame + #1 Provisions-Rechner-Link — kontert „€59 teuer" am Preis-Moment */}
            <p className="mt-5 text-sm leading-relaxed text-gray-600">
              {t('plan.roiNote')}
            </p>
            <Link
              href="/provisions-spar-rechner"
              className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-blue-600 transition-colors hover:text-blue-700 hover:underline"
            >
              {t('plan.commissionCalcLink')}
              <span aria-hidden="true">→</span>
            </Link>

            {/* CTA */}
            <div className="mt-7">
              <SignupCTA variant="primary" size="lg" fullWidth source="pricing">
                {t('plan.cta')}
                <span aria-hidden="true">→</span>
              </SignupCTA>
              <p className="mt-3 text-center text-xs text-gray-500">
                {t('plan.ctaSub')}
              </p>
            </div>

            {/* Quick-Start-Box entfernt (impeccable P1 2026-07-31): duplizierte
                HowItWorks mit ABWEICHENDEN Schritten — die Setup-Story erzählt
                jetzt nur noch die HowItWorks-Checkliste. quickStart.*-i18n-Keys
                bleiben für Mails/Onboarding erhalten. */}

            {/* Includes */}
            <p className={`mt-9 ${TYPE.labelMd} text-gray-500`}>
              {t('plan.includes')}
            </p>
            {/* Signature-Moment: Features haken sich beim Scroll-Einstieg
                nacheinander ein (sehr subtil, Codex 2026-07-31). Basis =
                sichtbar; Animation nur motion-safe nach Reveal-Trigger. */}
            <style>{`
@media (prefers-reduced-motion: no-preference) {
  [data-revealed] .prc-feat { animation: prcFeat .3s ease-out both; }
  @keyframes prcFeat {
    from { opacity: 0; transform: translateY(4px); }
  }
}
            `}</style>
            <ul role="list" className="mt-4 grid gap-2.5 sm:grid-cols-2">
              {(showAllFeatures ? features : features.slice(0, 7)).map((feat, i) => (
                <li
                  key={i}
                  className="prc-feat flex items-start gap-2.5 text-sm text-gray-700"
                  style={{ animationDelay: `${Math.min(i, 14) * 45}ms` }}
                >
                  <span
                    className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600"
                    aria-hidden="true"
                  >
                    <Check className="h-3 w-3" />
                  </span>
                  {feat}
                </li>
              ))}
            </ul>
            {!showAllFeatures && (
              <button
                type="button"
                onClick={() => setShowAllFeatures(true)}
                className="mt-3 text-sm font-medium text-blue-600 transition-colors hover:text-blue-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 rounded-sm"
              >
                {t('plan.showAllFeatures', { count: featureCount })}
              </button>
            )}

            {/* Addons */}
            <p className={`mt-8 ${TYPE.labelMd} text-gray-500`}>
              {t('plan.optional')}
            </p>
            <ul role="list" className="mt-3 space-y-2">
              {addons.map((addon, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-sm text-gray-500"
                >
                  <span
                    className="mt-0.5 text-gray-400 font-bold"
                    aria-hidden="true"
                  >
                    +
                  </span>
                  {addon}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>

      <Reveal delay={160}>
        <p className="relative mx-auto mt-8 max-w-2xl text-center text-sm text-gray-500">
          {t('plan.reassurance')}
        </p>
      </Reveal>
    </Section>
  );
}
